import { useQuery } from '@tanstack/react-query';
import * as S from '@blisspointmedia/bpm-types/dist/StreamingPerformance';
import { DateRange } from '@blisspointmedia/bpm-types/dist/RelativeDatePresets';
import { GeoDataMap } from '@Components/DmaMap/DmaMap';
import { awaitJSON, ToolsLambdaFetch } from '@src/utils/fetch-utils';
import { Lag, LAGS } from './geo-response-constants';

// Sends the cross-channel KPI label as-is; tools/geo.js resolves it to the raw per-source KPI id(s).
const fetchGeoLags = async (kpi: string, company: string, channel: S.Prefix): Promise<Lag[]> => {
  const response = await ToolsLambdaFetch('/geo_lags', {
    params: { kpi, company, prefix: channel }
  });
  const lags = await awaitJSON<string[]>(response);
  return LAGS.filter(lag => lags.includes(lag));
};

export const useGeoLags = (
  kpi: string | undefined,
  company: string,
  channel: S.Prefix
): ReturnType<typeof useQuery<Lag[]>> =>
  useQuery({
    queryKey: ['geoLags', channel, company, kpi],
    queryFn: () => fetchGeoLags(kpi as string, company, channel),
    enabled: Boolean(kpi && company),
    // GeoResponse.tsx already surfaces this query's error via useSetError — suppress the
    // global QueryCache fallback (src/main.tsx) so it doesn't double-show the dialog.
    meta: { suppressGlobalError: true }
  });

const fetchGeoData = async (
  kpi: string,
  company: string,
  lag: Lag,
  channel: S.Prefix,
  dateRange: DateRange
): Promise<GeoDataMap> => {
  const response = await ToolsLambdaFetch('/geo', {
    params: {
      kpi,
      company,
      lag,
      prefix: channel,
      startDate: dateRange.start,
      endDate: dateRange.end
    }
  });
  return awaitJSON<GeoDataMap>(response);
};

export const useGeoResponseData = (
  kpi: string | undefined,
  company: string,
  lag: Lag | undefined,
  channel: S.Prefix,
  dateRange: DateRange
): ReturnType<typeof useQuery<GeoDataMap>> =>
  useQuery({
    queryKey: ['geoResponseData', channel, company, kpi, lag, dateRange.start, dateRange.end],
    queryFn: () => fetchGeoData(kpi as string, company, lag as Lag, channel, dateRange),
    enabled: Boolean(kpi && company && lag),
    // Same as useGeoLags above: the page shows this error itself.
    meta: { suppressGlobalError: true }
  });
